import React, { useState, useEffect } from 'react';
import { AGENT_SEARCH_PATH, EXP_COMMERCIAL_PATH } from 'utils/agent-endpoint';
import Button, { SECONDARY_THEME } from 'components/control/button';
import { AgentFull, fetchAgent } from 'utils/agent-endpoint';
import BackArrowIcon from 'components/icon/back-arrow-icon';
import FacebookIcon from 'components/icon/facebook-icon';
import LanguageIcon from '@mui/icons-material/Language';
import CopyButton from 'components/control/copy-button';
import { buildClassName } from 'utils/build-class-name';
import PhoneIcon from 'components/icon/phone-icon';
import MailIcon from 'components/icon/mail-icon';
import useSizeClass from 'hooks/use-size-class';
import styles from './style.module.scss';
import { useRouter } from 'next/router';
import NextImage from 'next/image';
import Link from 'components/link';
import SocialLink from './social-link';
import { agentSearchIds } from 'constants/test-constants';
import LinkedInIcon from 'components/icon/linkedin-icon';
import TwitterIcon from 'components/icon/twitter-icon';
import InstagramIcon from 'components/icon/instagram-icon';
import YoutubeIcon from 'components/icon/youtube-icon';
import TiktokIcon from 'components/icon/tiktok-icon';
import { agentAssociations, militaryBadgeMapping } from 'constants/military-badge-mappings';
import { getMilitaryAssociationDisplayName } from 'utils/military-utils';
import { useRecaptcha } from 'hooks/use-recaptcha';
import { AgentSocialMediaLinks } from 'constants/agent-profile';
import { findUUIDWithinStrings } from 'utils/uuid/uuid';
import { useTranslation } from 'next-i18next';
import { AGENT_SEARCH_NAMESPACE, LOCALE_AGENT_SEARCH_PATHS } from 'types/locales';
import { useTenantId } from 'hooks/use-tenant-id';
import { getTenantById } from 'tenants/manager';
import { useLocale } from 'hooks/use-locale';
import { useAgentTranslation } from 'hooks/use-agent-translation';
import LoadingSpinner from 'components/loading-spinner';
import { Alert } from '@mui/material';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { AGENT_PROFILE_TRANSLATION_FLAG } from 'constants/feature-flags';
import { ProvinceAndState } from 'utils/province_or_state';
import { getAgentLicense } from 'utils/agent-license-utils';
import { AgentObservabilityService } from 'observability';
import InitialsAvatar from 'components/initials-avatar';
import ExpCommercialIcon from 'components/icon/exp-commercial-icon';

export const styleRichContent = (content?: string) => {
  if (!content) return null;
  const cleanedContent = content.replace(/<\/?pre>/g, ''); // Remove <pre> and </pre> tags from the content
  const lines = cleanedContent.split('\n');
  const styledLines = lines.map(line => `<span style="display: block; padding-bottom: 1em;">${line}</span>`).join('');
  return styledLines;
};

interface AgentPageProps {
  eventTrackers: {
    onPageView: () => void;
    onGoBack: () => void;
    onSocialMediaLinkClick: (AgentSocialMediaLinks: AgentSocialMediaLinks) => void;
    onCopyEmailClick: () => void;
    onCopyPhoneClick: () => void;
    onExploreServicesClick: () => void;
    onJoinExpClick: () => void;
  };
  joinUsLink: string;
  isCommercial: boolean;
}

function AgentPage({ eventTrackers, joinUsLink, isCommercial }: AgentPageProps) {
  const router = useRouter();
  const { t } = useTranslation(AGENT_SEARCH_NAMESPACE);
  const tenantId = useTenantId();
  const tenant = getTenantById(tenantId);
  const { currentLocale } = useLocale(tenantId);
  const isNonDefaultLocale = currentLocale !== tenant.localization.defaultLanguage;
  const growthbook = useGrowthBook();

  const isMobile = useSizeClass({ max: 600 });
  
  // Split agent info into static (renders immediately) and dynamic (may need translation)
  const [staticAgentInfo, setStaticAgentInfo] = useState<Omit<AgentFull, 'bio' | 'specializations' | 'designations'> | null>(null);
  const [dynamicAgentInfo, setDynamicAgentInfo] = useState<Pick<AgentFull, 'bio' | 'specializations' | 'designations'> | null>(null);
  
  const { scriptLoaded, RecaptchaScript } = useRecaptcha();
  const { onPageView, onGoBack, onSocialMediaLinkClick, onCopyEmailClick, onCopyPhoneClick, onExploreServicesClick, onJoinExpClick } = eventTrackers;

  const agentInfoFromQuery = router.query.dynamic && router.query.dynamic as string[];
  const agentId = findUUIDWithinStrings(agentInfoFromQuery);
  const agentPathname = isCommercial ? EXP_COMMERCIAL_PATH : AGENT_SEARCH_PATH;

  // Check if translation feature is enabled via Growthbook
  const isTranslationEnabled = growthbook.getFeatureValue(AGENT_PROFILE_TRANSLATION_FLAG, false);

  // Only enable translation once dynamic content is loaded AND feature flag is enabled
  const shouldEnableTranslation = isTranslationEnabled && isNonDefaultLocale && dynamicAgentInfo !== null;

  // Use translation hook to translate dynamic content for non-default locales
  const { translatedContent, isTranslating, hasError } = useAgentTranslation({
    content: dynamicAgentInfo,
    agentId: agentId,
    targetLanguage: currentLocale,
    sourceLanguage: tenant.localization.defaultLanguage,
    enabled: shouldEnableTranslation,
  });

  const [imgSrc, setImgSrc] = useState<string | null>(staticAgentInfo?.photo?.trim() || null);

  useEffect(() => {
    if (!agentId) {
      router.push(agentPathname);
      return;
    }

    if (!scriptLoaded) return;

    fetchAgent(agentId).then((result: AgentFull | Error) => {
      if (result instanceof Error) {
        AgentObservabilityService.trackProfileViewFailure({
          error: result,
          agentId,
        });
        console.error(result);
      } else {
        // Split agent data: static info renders immediately, dynamic info waits for translation
        const { bio, specializations, designations, ...staticInfo } = result;
        
        // Set static info first - this renders immediately
        setStaticAgentInfo(staticInfo);
        setImgSrc(result.photo?.trim() || null);
        
        // Set dynamic info separately - translation hook will handle this
        setDynamicAgentInfo({ bio, specializations, designations });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded, agentId]);

  useEffect(() => {
    // Track page view when the component mounts
    onPageView();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoBack = () => {
    onGoBack();
    if (router.query.prevPath) {
      router.push(router.query.prevPath as string);
    } else {
      router.push(agentPathname);
    }
  };

  // Use translated content if available, otherwise fall back to original (but only after translation completes or fails)
  // During translation, hide content to avoid showing untranslated text
  // If feature flag is disabled and non-default locale, hide dynamic fields (original behavior)
  const shouldHideDynamicFields = !isTranslationEnabled && isNonDefaultLocale;
  const displayBio = shouldHideDynamicFields ? null : (isTranslating ? null : (translatedContent?.bio ?? dynamicAgentInfo?.bio));
  const displaySpecializations = shouldHideDynamicFields ? null : (isTranslating ? null : (translatedContent?.specializations ?? dynamicAgentInfo?.specializations));
  const displayDesignations = shouldHideDynamicFields ? null : (isTranslating ? null : (translatedContent?.designations ?? dynamicAgentInfo?.designations));
  
  const styledBio = styleRichContent(displayBio);
  
  const socialLinks = [
    { type: AgentSocialMediaLinks.FACEBOOK, link: staticAgentInfo?.facebook, Icon: FacebookIcon, label: t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_FACEBOOK, { defaultValue: 'Facebook' }) },
    { type: AgentSocialMediaLinks.WEBSITE, link: staticAgentInfo?.website, Icon: LanguageIcon, label: t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_WEBSITE, { defaultValue: 'Website' }) },
    { type: AgentSocialMediaLinks.LINKEDIN, link: staticAgentInfo?.linkedIn, Icon: LinkedInIcon, label: t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_LINKEDIN, { defaultValue: 'LinkedIn' }) },
    { type: AgentSocialMediaLinks.TWITTER, link: staticAgentInfo?.twitter, Icon: TwitterIcon, label: t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_TWITTER, { defaultValue: 'Twitter' }), className: styles['twitter-button'] },
    { type: AgentSocialMediaLinks.INSTAGRAM, link: staticAgentInfo?.instagram, Icon: InstagramIcon, label: t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_INSTAGRAM, { defaultValue: 'Instagram' }) },
    { type: AgentSocialMediaLinks.YOUTUBE, link: staticAgentInfo?.youtube, Icon: YoutubeIcon, label: t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_YOUTUBE, { defaultValue: 'YouTube' }), className: styles['youtube-button'] },
    { type: AgentSocialMediaLinks.TIKTOK, link: staticAgentInfo?.tiktok, Icon: TiktokIcon, label: t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_TIKTOK, { defaultValue: 'Tiktok' }) },
  ];

  const handleMobileEmailButtonClick = (email: string) => {
    onCopyEmailClick();
    window.open(`mailto:${email}`);
  };
  const handleMobilePhoneButtonClick = (phone: string) => {
    onCopyPhoneClick();
    window.location.href = `tel:${phone}`;
  };

  const milnetAssociationName = getMilitaryAssociationDisplayName(staticAgentInfo?.milnet?.milnetAssociation);

  // Check if agent has Quebec license
  const hasQuebecLicense = staticAgentInfo?.license?.some(license => license.state === ProvinceAndState.QC) || staticAgentInfo?.state === ProvinceAndState.QC;
  const agentLicense = getAgentLicense(staticAgentInfo);

  return (
    <div className={styles.component}>
      <a onClick={handleGoBack} className={styles['top-back-button']}>
        <BackArrowIcon className={styles['back-arrow']} /> <span>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_RETURN_TO_SEARCH, { defaultValue: 'Return to Search' })}</span>
      </a>
      <div className={buildClassName(styles.content, isCommercial && styles['is-commercial'])}>
        <div className={styles['main-info']}>
          <h1 data-testid={agentSearchIds.agentProfileFirstName}>{staticAgentInfo?.firstName}</h1>
          <h1 data-testid={agentSearchIds.agentProfileLastName}>{staticAgentInfo?.lastName}</h1>
          {agentLicense && (
            <p className={styles['license-number']} data-testid={agentSearchIds.agentProfileLicense}>
              {agentLicense}
            </p>
          )}
          <div className={styles['agent-picture']}>
            {imgSrc ? (
              <NextImage
                alt={'agent-picture'}
                src={imgSrc}
                onError={() => setImgSrc(null)}
                priority
                fill
                sizes='(max-width: 639px) 480px, 640px'
              />
            ) : isCommercial ? (
              <div className={styles['agent-picture-exp-logo']} role="img" aria-label={`${staticAgentInfo?.firstName} ${staticAgentInfo?.lastName}`}>
                <ExpCommercialIcon />
              </div>
            ) : (
              <InitialsAvatar
                fill
                firstName={staticAgentInfo?.firstName}
                lastName={staticAgentInfo?.lastName}
                className={styles['agent-picture-initials']}
              />
            )}
          </div>
        </div>
        <div className={styles['bio-container']}>
          <div className={styles['bio']}>
            <h2 className={styles['bio-heading']}>
              {t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_CONTACT, { defaultValue: 'Contact' })}
            </h2>
            <div className={styles['bio-content']} data-testid={agentSearchIds.agentProfileContactInfo}>
              {!isMobile ?
                <>
                  {staticAgentInfo?.email &&
                    <div className={styles['personal-contact']}>
                      <p>{staticAgentInfo.email}</p>
                      <CopyButton content={staticAgentInfo.email} tooltipText={t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_TOOLTIPS_EMAIL_COPIED, { defaultValue: 'Email Copied' })} onClick={onCopyEmailClick} />
                    </div>
                  }
                  {staticAgentInfo?.phoneNumber &&
                    <div className={styles['personal-contact']}>
                      <p>{staticAgentInfo.phoneNumber}</p>
                      <CopyButton content={staticAgentInfo.phoneNumber} tooltipText={t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_TOOLTIPS_NUMBER_COPIED, { defaultValue: 'Number Copied' })} onClick={onCopyPhoneClick} />
                    </div>
                  }
                </>
                :
                <>
                  <div className={styles['social-media']}>
                    {staticAgentInfo?.email &&
                      <Button
                        Icon={MailIcon}
                        theme={SECONDARY_THEME}
                        label={t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_EMAIL, { defaultValue: 'Email' })}
                        className={styles['custom-button-style']}
                        onClick={() => handleMobileEmailButtonClick(staticAgentInfo.email)}
                      />
                    }
                    {staticAgentInfo?.phoneNumber &&
                      <Button
                        Icon={PhoneIcon}
                        theme={SECONDARY_THEME}
                        label={t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_PHONE, { defaultValue: 'Phone' })}
                        className={styles['custom-button-style']}
                        onClick={() => handleMobilePhoneButtonClick(staticAgentInfo.phoneNumber)}
                      />
                    }
                  </div>
                  <br/>
                </>
              }
              <div className={styles['social-media']}>
                {socialLinks.map(({ type, link, label, Icon, className }) => link?.trim() &&
                  <SocialLink
                    key={type}
                    link={link}
                    label={label}
                    Icon={Icon}
                    className={buildClassName(styles['custom-button-style'], className)}
                    onClick={() => onSocialMediaLinkClick(type)}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={styles['bio']}>
            <h2 className={styles['bio-heading']}>
              {t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_ABOUT, { defaultValue: 'About' })}
            </h2>
            <div className={styles['bio-content']} data-testid={agentSearchIds.agentProfileAbout}>
              {staticAgentInfo?.city && staticAgentInfo?.state &&
                <div className={styles['bio-content-heading']}>
                  <p>
                    {staticAgentInfo.city}, {staticAgentInfo.state} {isCommercial ? t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_AGENT_TYPES_COMMERCIAL_REAL_ESTATE_ADVISOR, { defaultValue: 'Commercial Real Estate Advisor' }) : t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_AGENT_TYPES_REAL_ESTATE_AGENT, { defaultValue: 'Real Estate Agent' })}
                  </p>
                </div>
              }
              {isTranslating && dynamicAgentInfo?.bio ? (
                <div className={styles['about']}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '0.5em' }}>
                    <div style={{ transform: 'scale(0.3)', transformOrigin: 'left center' }}>
                      <LoadingSpinner />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {hasError && dynamicAgentInfo?.bio && (
                    <Alert severity="error" style={{ marginBottom: '1em' }}>
                      {t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_TRANSLATION_ERROR, {
                        defaultValue: 'Translation failed. Showing original content.',
                      })}
                    </Alert>
                  )}
                  {styledBio &&
                    <div className={styles['about']}>
                      <div className={styles['about-inner-html']} dangerouslySetInnerHTML={{ __html: styledBio }} />
                    </div>
                  }
                </>
              )}
            </div>
          </div>
          {(!!staticAgentInfo?.milnet && milnetAssociationName) &&
            <div className={styles['associations']}>
              <h2 className={styles['associations-heading']}>
                {t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_ASSOCIATIONS, { defaultValue: 'Associations' })}
              </h2>
              <div className={styles['associations-content']}>
                <p className={styles['associations-content-paragraph']}>
                  {milnetAssociationName}
                </p>
                <img
                  className={styles['associations-badge-image']}
                  src={agentAssociations[militaryBadgeMapping[staticAgentInfo?.milnet.milnetAssociation]]}
                  alt={staticAgentInfo?.milnet.milnetAssociation}
                />
              </div>
            </div>
          }
          {(!!staticAgentInfo?.languages?.length || !!displaySpecializations?.length || !!staticAgentInfo?.activeLocations?.length) &&
            <div className={styles['bio']}>
              <h2 className={styles['bio-heading']}>
                {t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_DESCRIPTIONS, { defaultValue: 'Descriptions' })}
              </h2>
              <div className={styles['bio-content']}>
                {!!staticAgentInfo?.languages.length &&
                  <>
                    <div className={styles['bio-content-heading']}>
                      <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_FIELDS_LANGUAGES, { defaultValue: 'Languages' })}</p>
                    </div>
                    <div className={styles['about']}>
                      <p>
                        {staticAgentInfo.languages.join(', ')}
                      </p>
                    </div>
                    <br/><br/>
                  </>
                }
                {(isTranslating && dynamicAgentInfo?.specializations?.length) ? (
                  <>
                    <div className={styles['bio-content-heading']}>
                      <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_FIELDS_SPECIALIZATIONS, { defaultValue: 'Specializations' })}</p>
                    </div>
                    <div className={styles['about']}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                        <div style={{ transform: 'scale(0.3)', transformOrigin: 'left center' }}>
                          <LoadingSpinner />
                        </div>
                        <span style={{ fontStyle: 'italic', fontSize: '0.9em', color: '#666' }}>
                          Translating specializations...
                        </span>
                      </div>
                    </div>
                    <br/><br/>
                  </>
                ) : (
                  <>
                    {hasError && dynamicAgentInfo?.specializations?.length && (
                      <Alert severity="error" style={{ marginBottom: '1em' }}>
                        {t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_TRANSLATION_ERROR, {
                          defaultValue: 'Translation failed. Showing original content.',
                        })}
                      </Alert>
                    )}
                    {!!displaySpecializations?.length &&
                      <>
                        <div className={styles['bio-content-heading']}>
                          <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_FIELDS_SPECIALIZATIONS, { defaultValue: 'Specializations' })}</p>
                        </div>
                        <div className={styles['about']}>
                          <p>
                            {displaySpecializations.join(', ')}
                          </p>
                        </div>
                        <br/><br/>
                      </>
                    }
                  </>
                )}
                {!!staticAgentInfo?.certifications?.length &&
                  <>
                    <div className={styles['bio-content-heading']}>
                      <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_FIELDS_CERTIFICATIONS, { defaultValue: 'Certifications' })}</p>
                    </div>
                    <div className={styles['about']}>
                      <p>
                        {staticAgentInfo.certifications.join(', ')}
                      </p>
                    </div>
                    <br/><br/>
                  </>
                }
                {(isTranslating && dynamicAgentInfo?.designations?.length) ? (
                  <>
                    <div className={styles['bio-content-heading']}>
                      <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_FIELDS_DESIGNATIONS, { defaultValue: 'Designations' })}</p>
                    </div>
                    <div className={styles['about']}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                        <div style={{ transform: 'scale(0.3)', transformOrigin: 'left center' }}>
                          <LoadingSpinner />
                        </div>
                        <span style={{ fontStyle: 'italic', fontSize: '0.9em', color: '#666' }}>
                          Translating designations...
                        </span>
                      </div>
                    </div>
                    <br/><br/>
                  </>
                ) : (
                  <>
                    {hasError && dynamicAgentInfo?.designations?.length && (
                      <Alert severity="error" style={{ marginBottom: '1em' }}>
                        {t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_TRANSLATION_ERROR, {
                          defaultValue: 'Translation failed. Showing original content.',
                        })}
                      </Alert>
                    )}
                    {!!displayDesignations?.length &&
                      <>
                        <div className={styles['bio-content-heading']}>
                          <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_FIELDS_DESIGNATIONS, { defaultValue: 'Designations' })}</p>
                        </div>
                        <div className={styles['about']}>
                          <p>
                            {displayDesignations.join(', ')}
                          </p>
                        </div>
                        <br/><br/>
                      </>
                    }
                  </>
                )}
                {!!staticAgentInfo?.activeLocations?.length &&
                  <>
                    <div className={styles['bio-content-heading']}>
                      <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_FIELDS_LOCATIONS_SERVED, { defaultValue: 'Locations Served' })}</p>
                    </div>
                    <div className={styles['about']}>
                      <p>
                        {staticAgentInfo.activeLocations.map(location => `${location.city}, ${location.state}`).join('; ')}
                      </p>
                    </div>
                  </>
                }
              </div>
            </div>
          }
          {!isCommercial ? (
            <>
              <div className={buildClassName(styles['bio'], styles['bio-small-margin'])}>
                <h2 className={styles['bio-heading']}/>
                <div className={styles['bio-content']}>
                  <div className={buildClassName(styles['learn-more'], styles['learn-more-no-bg'])}>
                    <div className={styles['copy']}>
                      <h3>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_CTA_EXPLORE_SERVICES_TITLE, { defaultValue: 'Explore Our Services' })}</h3>
                      <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_CTA_EXPLORE_SERVICES_DESCRIPTION, {
                        context: hasQuebecLicense ? 'qc' : undefined,
                        defaultValue: hasQuebecLicense 
                          ? 'Are you a real estate broker? View the services that eXp can provide you as an eXp broker.'
                          : 'Are you a real estate agent? View the services that eXp can provide you as an eXp agent.',
                      })}</p>
                    </div>
                    <div className={styles['cta']}>
                      <Link href={'/explore-exp'}>
                        <Button label={t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_LEARN_MORE, { defaultValue: 'Learn More' })}
                          theme={SECONDARY_THEME}
                          onClick={onExploreServicesClick} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles['bio']}>
                <h2 className={styles['bio-heading']}/>
                <div className={styles['bio-content']}>
                  <div className={styles['learn-more']}>
                    <div className={styles['copy']}>
                      <h3>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_CTA_JOIN_EXP_TITLE, { defaultValue: 'Join eXp Realty' })}</h3>
                      <p>{t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_CTA_JOIN_EXP_DESCRIPTION, { defaultValue: 'Your success fuels our obsession for pushing the real estate industry beyond its edges.' })}</p>
                    </div>
                    <div className={styles['cta']}>
                      <Link href={joinUsLink} target='_blank'>
                        <Button label={t(LOCALE_AGENT_SEARCH_PATHS.PROFILE_BUTTONS_LEARN_MORE, { defaultValue: 'Learn More' })}
                          theme={SECONDARY_THEME}
                          onClick={onJoinExpClick} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : <div style={{ height: '300px' }}/>}
        </div>
      </div>
      {RecaptchaScript}
    </div>
  );
}

export default AgentPage;